/**
 * Container component to cover dummy components
 *
 * @author: David-Julian Buch
 */

import * as React from 'react';
import {
  ScrollViewComponent,
  ScrollViewProps,
  StyleSheet,
  View,
  ViewProps,
  VirtualizedListProps,
} from 'react-native';
import {
  KeyboardAwareScrollView,
  KeyboardAwareScrollViewProps,
} from 'react-native-keyboard-aware-scroll-view';

import { ScrollView } from 'react-native-virtualized-view';
import { Padding } from '@/GlobalStyles';
import { TextInput as NativeTextInput } from 'react-native/Libraries/Components/TextInput/TextInput';
import { TextInputFormProps } from '@/components/Form/types';

type BaseContainerProps = (
  | {
      withScrollView?: 'simple';
      outerViewProps?: Omit<VirtualizedListProps<any>, 'renderItem' | 'data'>;
    }
  | {
      withScrollView: 'keyboardAware';
      outerViewProps?: KeyboardAwareScrollViewProps;
    }
  | {
      withScrollView: false;
      outerViewProps?: ViewProps;
    }
) & {
  children?: React.ReactNode;
  padding?: number;
};

type OuterViewType = typeof View | typeof ScrollView | typeof KeyboardAwareScrollView | undefined;
type OuterViewPros = ViewProps | VirtualizedListProps<any> | KeyboardAwareScrollViewProps;

const BaseContainer = React.forwardRef<OuterViewType, BaseContainerProps>(
  (props: BaseContainerProps, ref): JSX.Element | null => {
    if (!props) {
      return null;
    }
    const { withScrollView, outerViewProps, children, padding } = props;
    /**
   First option around dummy data - View
   */
    let OuterView: OuterViewType = View;
    let finalOuterProps: OuterViewPros;
    /**
   Second and third options around dummy data - ScrollView or KeyboardAwareScrollView
   */
    if (withScrollView === undefined || withScrollView === 'simple') {
      OuterView = ScrollView;
    } else if (withScrollView === 'keyboardAware') {
      OuterView = KeyboardAwareScrollView;
    }

    /**
   Applied style depending on boolean of withScrollView
   */
    if (withScrollView === false) {
      /**
     ____
     */
      const { style: outerViewStyle, ...otherOuterViewProps } = outerViewProps || {};
      const finalOuterViewStyle = StyleSheet.flatten([
        styles.outerBaseStyle,
        { flex: 1 },
        outerViewStyle,
      ]);
      finalOuterProps = { style: finalOuterViewStyle, ...otherOuterViewProps };
    } else {
      const {
        style: scrollViewStyle,
        contentContainerStyle: outerViewStyle,
        ...otherOuterViewProps
      } = (outerViewProps as VirtualizedListProps<any>) || {};
      const finalOuterViewStyle = StyleSheet.flatten([
        styles.outerBaseStyle,
        outerViewStyle,
        padding ? { padding } : null,
      ]);
      const finalScrollViewStyle = StyleSheet.flatten([
        { marginHorizontal: -Padding.p_13xl, paddingHorizontal: Padding.p_13xl },
        scrollViewStyle,
        { paddingBottom: 70 },
      ]);

      finalOuterProps = {
        ListHeaderComponentStyle: finalScrollViewStyle,
        nestedScrollEnabled: true,
        contentContainerStyle: finalOuterViewStyle,
        removeClippedSubviews: false,
        ...otherOuterViewProps,
      };
      if (withScrollView === 'keyboardAware') {
        finalOuterProps = {
          enableOnAndroid: true,
          nestedScrollEnabled: true,
          style: finalOuterViewStyle,
          //bug fix : https://github.com/APSL/react-native-keyboard-aware-scroll-view/issues/571
          keyboardOpeningTime: Number.MAX_VALUE,
          extraScrollHeight: 20,
          ...finalOuterProps,
        };
      }
    }

    return (
      <OuterView {...finalOuterProps} ref={ref}>
        {children}
      </OuterView>
    );
  }
);

export default BaseContainer;

const styles = StyleSheet.create({
  outerBaseStyle: {
    flexGrow: 1,
    paddingBottom: Padding.p_13xl,
  },
});
